import { createFileRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import * as z from 'zod';
import { iamService } from '@/api/services/iam';
import { validateParams } from '@/router/utils';

/**
 * The role editor's tab layout: it keeps out ids that are not roles of this account, and forwards the
 * bare `$roleId` URL to the permissions tab — landing on the layout itself would render the tab bar
 * over an empty outlet. Permissions is always a reachable default: getting in here already takes
 * `read:roles`, which is all that tab needs.
 */
export const Route = createFileRoute('/_authenticated/_dashboard/accounts/$accountId/cloud-access/roles/$roleId')({
  params: validateParams(
    z.object({
      roleId: z.guid(),
    }),
  ),
  beforeLoad: async ({ context: { queryClient }, params, matches }) => {
    const { roleId, accountId } = params;

    // Fetching the roles runs the permissions interceptor, which throws an AccessControlError
    // (handled as a not-found) when the user lacks `read:roles`.
    const { items: accountRoles } = await queryClient.fetchQuery(iamService.listRolesQuery({ accountId }));

    if (!accountRoles.some((role) => role.id === roleId)) {
      throw redirect({
        to: '/accounts/$accountId/cloud-access',
        params: { accountId },
        replace: true,
      });
    }

    if (matches.at(-1)?.routeId === Route.id) {
      throw redirect({
        to: '/accounts/$accountId/cloud-access/roles/$roleId/permissions',
        params,
        replace: true,
      });
    }
  },
  component: lazyRouteComponent(() => import('./route.component'), 'RoleIdComponent'),
});
